import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { countryCodes } from '../mock';
import InnerPageHero from '../components/InnerPageHero';
import InnerPageBanner from '../components/InnerPageBanner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    contactNumber: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    toast.success('Thank you! We will contact you shortly.');
    setFormData({
      name: '',
      email: '',
      countryCode: '+1',
      contactNumber: '',
      message: ''
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Trusted Trademark Filer - Get Expert Help</title>
        <meta name="description" content="Contact Trusted Trademark Filer for trademark registration assistance. Call +1 (888) 203 1471 or email Info@trustedtrademarkfiler.com. We're here to help protect your brand." />
      </Helmet>
      <div className="min-h-screen">
      {/* Hero Section */}
      <InnerPageHero
        badge="CONTACT US"
        title="Get In Touch"
        description="Have questions about trademark registration? Our team is here to help you protect your brand with expert guidance and support."
      />

      {/* Contact Form and Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Send Us a Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Enter your full name"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="Enter your email address"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="contact">Contact Number *</Label>
                  <div className="flex gap-2 mt-1">
                    <Select
                      value={formData.countryCode}
                      onValueChange={(value) => handleChange('countryCode', value)}
                    >
                      <SelectTrigger className="w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countryCodes.map((item) => (
                          <SelectItem key={item.code} value={item.code}>
                            {item.code} ({item.country})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="contact"
                      type="tel"
                      required
                      value={formData.contactNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value.length <= 10) {
                          handleChange('contactNumber', value);
                        }
                      }}
                      placeholder="Enter Your Number"
                      className="flex-1"
                      maxLength={10}
                      pattern="\d{10}"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    required
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    placeholder="Tell us how we can help you..."
                    rows={6}
                    className="mt-1"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#FF4A2A] hover:bg-[#E63E1F] text-white py-6 text-lg font-semibold"
                >
                  Send Message
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-6">
                  Contact Information
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Reach out to us through any of these channels. We're here to answer your questions and help protect your brand.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-[#E2F8FE] p-4 rounded-lg">
                    <Mail className="h-6 w-6 text-[#FF4A2A]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      Email Us
                    </h3>
                    <p className="text-gray-600">
                      Info@trustedtrademarkfiler.com
                    </p>
                    <p className="text-gray-600">
                      support@trustedtrademarkfiler.com
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#E2F8FE] p-4 rounded-lg">
                    <Phone className="h-6 w-6 text-[#FF4A2A]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      Call Us
                    </h3>
                    <p className="text-gray-600">
                      +1 (888) 203 1471
                    </p>
                    <p className="text-gray-600">
                      +1 (888) 203 1471
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#E2F8FE] p-4 rounded-lg">
                    <MapPin className="h-6 w-6 text-[#FF4A2A]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      Visit Us
                    </h3>
                    <p className="text-gray-600">
                      28202 Cabot Rd #300<br />
                      Laguna Niguel, CA 92677<br />
                      United States
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#E2F8FE] to-white p-8 rounded-2xl shadow-lg mt-8">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Quick Response Guaranteed
                </h3>
                <p className="text-gray-600">
                  We typically respond to all inquiries within 24 hours during business days. For urgent matters, please call us directly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default ContactPage;